import './NotificationPage.scss';
import { Button, Card, Icon, Input, SimpleNumberCard, Textarea, Typography } from '@design-system/index';
import { AsyncDropdown, DropdownOption } from '@design-system/components/Dropdown/AsyncDropdown';
import { Formik } from 'formik';
import { NotificationLine } from '@icons';
import {
    useSendNotificationToAllMutation,
    useSendNotificationToMemberMutation,
    useSendNotificationToMembersMutation,
    useGetTokenStatsMutation
} from '@services/product/notification/notification.endpoints';
import { useGetProductsMutation } from '@services/product/product/product.endpoints';
import { useGetMembersMutation } from '@services/member/member/member.endpoints';
import { ProductTable } from '@services/product/product/product.types';
import { MemberTable } from '@services/member/member/member.types';
import {
    SendNotificationPayload,
    SendNotificationToMemberPayload,
    SendNotificationToMembersPayload
} from '@services/product/notification/notification.types';
import { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { HistoryPanel } from './HistoryPanel/HistoryPanel';

const MySwal = withReactContent(Swal);

type ActiveTab = 'send' | 'history';

export default function NotificationPage() {
    const [activeTab, setActiveTab] = useState<ActiveTab>('send');
    const [sendNotificationToAll, { isLoading: isSendingAll }] = useSendNotificationToAllMutation();
    const [sendNotificationToMember, { isLoading: isSendingMember }] = useSendNotificationToMemberMutation();
    const [sendNotificationToMembers, { isLoading: isSendingMembers }] = useSendNotificationToMembersMutation();
    const [getTokenStats, { data: statsResponse }] = useGetTokenStatsMutation();
    const isSending = isSendingAll || isSendingMember || isSendingMembers;
    const [selectedProduct, setSelectedProduct] = useState<DropdownOption | undefined>();
    const [selectedMembers, setSelectedMembers] = useState<DropdownOption[]>([]);
    const productUrlsRef = useRef<Record<string, string>>({});

    useEffect(() => {
        getTokenStats();
    }, []);

    const stats = statsResponse?.data;

    const handleSubmit = async (values: { title: string; body: string }, { resetForm }: { resetForm: () => void }) => {
        let targetMessage: string;
        if (selectedMembers.length === 1) {
            targetMessage = `<p>La notification sera envoyée à <strong>${selectedMembers[0].label}</strong>.</p>`;
        } else if (selectedMembers.length > 1) {
            targetMessage = `<p>La notification sera envoyée à <strong>${selectedMembers.length} adhérents</strong>.</p>`;
        } else {
            targetMessage = `<p>La notification sera envoyée à <strong>${stats?.total_tokens ?? 0} appareil(s)</strong>.</p>`;
        }

        const result = await MySwal.fire({
            title: 'Envoyer la notification ?',
            html: targetMessage,
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Envoyer',
            cancelButtonText: 'Annuler'
        });

        if (!result.isConfirmed) return;

        const dataPayload = selectedProduct
            ? { url: productUrlsRef.current[selectedProduct.id] }
            : undefined;

        if (selectedMembers.length === 1) {
            const payload: SendNotificationToMemberPayload = {
                memberId: Number(selectedMembers[0].id),
                title: values.title,
                body: values.body,
                ...(dataPayload && { data: dataPayload })
            };
            await sendNotificationToMember(payload);
        } else if (selectedMembers.length > 1) {
            const payload: SendNotificationToMembersPayload = {
                memberIds: selectedMembers.map((m) => Number(m.id)),
                title: values.title,
                body: values.body,
                ...(dataPayload && { data: dataPayload })
            };
            await sendNotificationToMembers(payload);
        } else {
            const payload: SendNotificationPayload = {
                title: values.title,
                body: values.body,
                ...(dataPayload && { data: dataPayload })
            };
            await sendNotificationToAll(payload);
        }
        resetForm();
        setSelectedProduct(undefined);
        setSelectedMembers([]);
        getTokenStats();
    };

    return (
        <div className="notification-page">
            <div className="notification-tabs">
                <Button
                    variant={activeTab === 'send' ? 'primary' : 'secondary'}
                    onClick={() => setActiveTab('send')}
                >
                    Envoyer
                </Button>
                <Button
                    variant={activeTab === 'history' ? 'primary' : 'secondary'}
                    onClick={() => setActiveTab('history')}
                >
                    Historique
                </Button>
            </div>

            {activeTab === 'send' && (
                <>
                    <div className="notification-stats">
                        <SimpleNumberCard
                            labelForSingle="Appareil enregistré"
                            labelForMultiple="Appareils enregistrés"
                            value={stats?.total_tokens ?? 0}
                        />
                        <SimpleNumberCard
                            labelForSingle="Adhérent associé"
                            labelForMultiple="Adhérents associés"
                            value={stats?.tokens_with_members ?? 0}
                        />
                    </div>

                    <Card
                        headerType="full"
                        headerTitle="Envoyer une notification push"
                        headerIcon={<Icon icon={<NotificationLine />} size={20} color="primary" />}
                    >
                        <Formik
                            initialValues={{ title: '', body: '' }}
                            onSubmit={handleSubmit}
                        >
                            {({ values, handleChange, handleSubmit }) => (
                                <form onSubmit={handleSubmit} className="form">
                                    <div className="form-section-container">
                                        <div className="form-section">
                                            <Typography intrinsic="h3">Contenu de la notification</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Titre"
                                                            name="title"
                                                            value={values.title}
                                                            required
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Message"
                                                            name="body"
                                                            required
                                                            value={values.body}
                                                            onChange={handleChange}
                                                            height={160}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <Typography intrinsic="h3">Destinataire(s) spécifique(s)</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <AsyncDropdown<MemberTable>
                                                            label="Sélectionner un ou plusieurs adhérents"
                                                            name="members"
                                                            isMulti
                                                            value={selectedMembers}
                                                            mutationHook={useGetMembersMutation}
                                                            mapDataToOptions={(data) =>
                                                                data.map((item) => ({
                                                                    id: item.id.toString(),
                                                                    label:
                                                                        item.cosemNumber +
                                                                        ' - ' +
                                                                        item.personalInformation.lastname +
                                                                        ' ' +
                                                                        item.personalInformation.firstname
                                                                }))
                                                            }
                                                            onOptionChange={(option) => {
                                                                if (Array.isArray(option)) {
                                                                    setSelectedMembers(option);
                                                                } else {
                                                                    setSelectedMembers([]);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <Typography intrinsic="h3">Lien vers un produit (optionnel)</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <AsyncDropdown<ProductTable>
                                                            label="Produit"
                                                            name="product"
                                                            value={selectedProduct}
                                                            menuPlacement="top"
                                                            mutationHook={useGetProductsMutation}
                                                            mapDataToOptions={(products) => {
                                                                products.forEach((p) => {
                                                                    productUrlsRef.current[String(p.id)] = p.publicLink;
                                                                });
                                                                return products.map((p) => ({
                                                                    id: String(p.id),
                                                                    label: p.presentation.title
                                                                }));
                                                            }}
                                                            onOptionChange={(option) => {
                                                                if (option && !Array.isArray(option)) {
                                                                    setSelectedProduct(option);
                                                                } else {
                                                                    setSelectedProduct(undefined);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="action-buttons">
                                        <Button
                                            type="submit"
                                            disabled={isSending || !values.title || !values.body}
                                            variant="primary"
                                        >
                                            Envoyer la notification
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </Card>
                </>
            )}

            {activeTab === 'history' && <HistoryPanel />}
        </div>
    );
}
